import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { AssistantModel, streamAssistantMessage } from '@/http/models/assistant/Assistant'
import {
  AssistantCapabilities,
  AssistantConversationSummary,
  AssistantEvent,
  AssistantLanguage,
  AssistantMessage,
  AssistantProposalSummary,
  AssistantUiAction,
  ProposalStatus,
} from '@/types/assistant'

import { assistantText, pageContextFor, reviewPathFor } from './assistantText'

const LANGUAGE_KEY = 'iws-admin:assistant-language'
const WIDTH_KEY = 'iws-admin:assistant-width'
const PANEL_MIN_WIDTH = 340
const PANEL_DEFAULT_WIDTH = 400
const WIDE_WIDTH = 720

/** Widest the panel may be: leaves at least 480px of page beside it. */
const panelMaxWidth = () => Math.max(PANEL_MIN_WIDTH, Math.min(960, window.innerWidth - 480))
const clampWidth = (w: number) => Math.round(Math.min(panelMaxWidth(), Math.max(PANEL_MIN_WIDTH, w)))

const readWidth = () => {
  try {
    const saved = Number(localStorage.getItem(WIDTH_KEY))
    return saved ? clampWidth(saved) : PANEL_DEFAULT_WIDTH
  } catch {
    return PANEL_DEFAULT_WIDTH
  }
}
const HIGHLIGHT_MS = 2600

const readLanguage = (): AssistantLanguage => {
  try {
    return localStorage.getItem(LANGUAGE_KEY) === 'bn' ? 'bn' : 'en'
  } catch {
    return 'en'
  }
}

/** Spotlights a control marked `data-copilot-id`, for a moment. */
const spotlight = (target: string) => {
  const el = document.querySelector<HTMLElement>(`[data-copilot-id="${CSS.escape(target)}"]`)
  if (!el) return
  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  el.classList.add('assistant-spotlight')
  window.setTimeout(() => el.classList.remove('assistant-spotlight'), HIGHLIGHT_MS)
}

interface AssistantContextValue {
  isOpen: boolean
  isExpanded: boolean
  /** Panel width in px on desktop (drag its left edge; remembered in this browser). */
  panelWidth: number
  setPanelWidth: (width: number) => void
  language: AssistantLanguage
  capabilities: AssistantCapabilities | null
  conversationUid: string | null
  title: string | null
  messages: AssistantMessage[]
  isBusy: boolean
  recents: AssistantConversationSummary[]
  open: () => void
  close: () => void
  toggleExpanded: () => void
  setLanguage: (language: AssistantLanguage) => void
  send: (text: string, source?: 'message' | 'voice') => void
  stop: () => void
  /** Sends the last message again after a failed reply. */
  retry: () => void
  newChat: () => void
  loadRecents: () => void
  openConversation: (conversationUid: string) => void
  deleteConversation: (conversationUid: string) => void
  rate: (messageId: number, rating: 1 | -1) => void
  /** Called by the review screens after the normal save (published) or when the merchant drops the change. */
  markProposal: (proposalUid: string, status: Exclude<ProposalStatus, 'pending'>) => Promise<void>
  /** Opens the screen that reviews a proposal (again, if it is already open). */
  openReview: (proposal: AssistantProposalSummary) => void
}

const AssistantContext = createContext<AssistantContextValue>(null!)

export const AssistantProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(false)
  const [panelWidth, setPanelWidthState] = useState(readWidth)
  // Expanded = wider than the default; the expand button jumps between the two.
  const isExpanded = panelWidth >= WIDE_WIDTH - 1
  const setPanelWidth = useCallback((width: number) => {
    const next = clampWidth(width)
    setPanelWidthState(next)
    try {
      localStorage.setItem(WIDTH_KEY, String(next))
    } catch (error) {
      console.warn('Assistant width not saved', error)
    }
  }, [])
  const [language, setLanguageState] = useState<AssistantLanguage>(readLanguage)
  const [capabilities, setCapabilities] = useState<AssistantCapabilities | null>(null)
  const [conversationUid, setConversationUid] = useState<string | null>(null)
  const [title, setTitle] = useState<string | null>(null)
  const [messages, setMessages] = useState<AssistantMessage[]>([])
  const [isBusy, setIsBusy] = useState(false)
  const [recents, setRecents] = useState<AssistantConversationSummary[]>([])
  const abortRef = useRef<AbortController | null>(null)
  const locationRef = useRef(location)
  locationRef.current = location

  useEffect(() => {
    AssistantModel.getCapabilities().then((res) => {
      if (res.success && res.data) setCapabilities(res.data.result)
    })
  }, [])

  const setLanguage = useCallback((next: AssistantLanguage) => {
    setLanguageState(next)
    try {
      localStorage.setItem(LANGUAGE_KEY, next)
    } catch (error) {
      console.warn('Assistant language not saved', error)
    }
  }, [])

  const updateLast = useCallback((change: (message: AssistantMessage) => AssistantMessage) => {
    setMessages((list) => (list.length ? [...list.slice(0, -1), change(list[list.length - 1])] : list))
  }, [])

  const runUiAction = useCallback((action: AssistantUiAction) => {
    if (action.action === 'navigate') {
      navigate(action.path)
    } else if (action.action === 'open_theme_editor') {
      const params = new URLSearchParams()
      if (action.page) params.set('page', action.page)
      if (action.section_id) params.set('section', action.section_id)
      if (action.panel) params.set('panel', action.panel)
      navigate(`/themes${params.toString() ? `?${params}` : ''}`)
    } else if (action.action === 'highlight') {
      // After a navigate in the same turn, give the page a moment to render.
      window.setTimeout(() => spotlight(action.target), 400)
    }
  }, [navigate])

  // A fresh `review` stamp each time, so opening the screen you are already on still brings the review back.
  const openReview = useCallback((proposal: AssistantProposalSummary) => {
    navigate(reviewPathFor(proposal), { state: { review: Date.now() } })
  }, [navigate])

  const handleEvent = useCallback((event: AssistantEvent) => {
    switch (event.type) {
      case 'meta':
        setConversationUid(event.conversation_uid)
        setTitle(event.title)
        break
      case 'text':
        updateLast((m) => ({ ...m, content: m.content + event.delta }))
        break
      case 'tool':
        updateLast((m) => event.state === 'start'
          ? { ...m, steps: [...m.steps, { name: event.name, done: false }] }
          : { ...m, steps: m.steps.map((s, i) => (i === m.steps.map((x) => x.name).lastIndexOf(event.name) ? { ...s, done: true, ok: event.ok } : s)) })
        break
      case 'card':
        updateLast((m) => ({ ...m, cards: [...m.cards, event.card] }))
        break
      case 'ui_action':
        runUiAction(event.action)
        break
      case 'proposal':
        updateLast((m) => ({ ...m, proposals: [...m.proposals, { ...event.proposal, status: 'pending' }] }))
        openReview(event.proposal)
        break
      case 'refused':
        updateLast((m) => ({ ...m, refused: true }))
        break
      case 'done':
        updateLast((m) => ({ ...m, id: event.message_id ?? m.id, streaming: false }))
        break
      case 'error':
        updateLast((m) => ({ ...m, error: event.message, streaming: false }))
        break
      default:
        break
    }
  }, [openReview, runUiAction, updateLast])

  const send = useCallback((text: string, source: 'message' | 'voice' = 'message') => {
    const message = text.trim()
    if (!message || isBusy) return
    setIsOpen(true)
    setIsBusy(true)
    const now = Date.now()
    setMessages((list) => [
      ...list,
      { id: `u${now}`, role: 'user', content: message, cards: [], proposals: [], steps: [] },
      { id: `m${now}`, role: 'model', content: '', cards: [], proposals: [], steps: [], streaming: true, live: true },
    ])

    const controller = new AbortController()
    abortRef.current = controller
    const { pathname, search } = locationRef.current
    streamAssistantMessage(
      { conversation_uid: conversationUid ?? undefined, message, language, source, page: pageContextFor(pathname, search) },
      handleEvent,
      controller.signal,
    )
      .catch((error: Error) => {
        if (controller.signal.aborted) return
        console.error('Assistant request failed', error)
        updateLast((m) => ({ ...m, error: error.message || assistantText(language).errorGeneric }))
      })
      .finally(() => {
        updateLast((m) => ({ ...m, streaming: false }))
        setIsBusy(false)
        abortRef.current = null
      })
  }, [conversationUid, handleEvent, isBusy, language, updateLast])

  const stop = useCallback(() => abortRef.current?.abort(), [])

  const retry = useCallback(() => {
    if (isBusy) return
    const lastUser = [...messages].reverse().find((m) => m.role === 'user')
    if (!lastUser) return
    const index = messages.lastIndexOf(lastUser)
    setMessages((list) => list.slice(0, index))
    // Next tick, so `send` appends after the removed pair.
    window.setTimeout(() => send(lastUser.content), 0)
  }, [isBusy, messages, send])

  const newChat = useCallback(() => {
    abortRef.current?.abort()
    setConversationUid(null)
    setTitle(null)
    setMessages([])
  }, [])

  const loadRecents = useCallback(() => {
    AssistantModel.getConversations().then((res) => {
      if (res.success && res.data) setRecents(res.data.result)
    })
  }, [])

  const openConversation = useCallback((uid: string) => {
    abortRef.current?.abort()
    AssistantModel.getConversation(uid).then((res) => {
      if (!res.success || !res.data) {
        toast.error(assistantText(language).errorGeneric)
        return
      }
      const c = res.data.result
      setConversationUid(c.conversation_uid)
      setTitle(c.title)
      setMessages(c.messages.map((m) => ({ id: m.id, role: m.role, content: m.content, cards: m.cards, proposals: [], steps: [], refused: m.refused, rating: m.rating })))
      setIsOpen(true)
    })
  }, [language])

  const deleteConversation = useCallback((uid: string) => {
    AssistantModel.deleteConversation(uid).then((res) => {
      if (!res.success) return
      setRecents((list) => list.filter((c) => c.conversation_uid !== uid))
      if (uid === conversationUid) newChat()
    })
  }, [conversationUid, newChat])

  const rate = useCallback((messageId: number, rating: 1 | -1) => {
    if (!conversationUid) return
    setMessages((list) => list.map((m) => (m.id === messageId ? { ...m, rating } : m)))
    AssistantModel.rateMessage(conversationUid, messageId, rating)
  }, [conversationUid])

  const markProposal = useCallback(async (proposalUid: string, status: Exclude<ProposalStatus, 'pending'>) => {
    setMessages((list) => list.map((m) => ({ ...m, proposals: m.proposals.map((p) => (p.proposal_uid === proposalUid ? { ...p, status } : p)) })))
    const res = await AssistantModel.resolveProposal(proposalUid, status)
    if (!res.success) console.error('Assistant: proposal status not saved', res.error)
  }, [])

  const value = useMemo<AssistantContextValue>(() => ({
    isOpen,
    isExpanded,
    panelWidth,
    setPanelWidth,
    language,
    capabilities,
    conversationUid,
    title,
    messages,
    isBusy,
    recents,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggleExpanded: () => setPanelWidth(isExpanded ? PANEL_DEFAULT_WIDTH : WIDE_WIDTH),
    setLanguage,
    send,
    stop,
    retry,
    newChat,
    loadRecents,
    openConversation,
    deleteConversation,
    rate,
    markProposal,
    openReview,
  }), [isOpen, isExpanded, panelWidth, setPanelWidth, language, capabilities, conversationUid, title, messages, isBusy, recents, setLanguage, send, stop, retry, newChat, loadRecents, openConversation, deleteConversation, rate, markProposal, openReview])

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>
}

export const useAssistant = () => useContext(AssistantContext)
